/**
 * Configures accessibility features for a Swiper instance, improving usability for assistive technologies by updating ARIA attributes and managing live region announcements.
 *
 * @param {Object} swiper - An instance of Swiper slider. The implementation ensures proper accessibility is set for the container and its slides.
 *
 * @return {Function|undefined} A teardown function or undefined when initialization fails.
 */
const setupSwiperAccessibility = ( swiper ) => {

	if ( !swiper || typeof swiper !== 'object' || !swiper.el ) {

		return;

	}

	const liveRegion = document.getElementById( 'live-region' );

	/**
	 * Updates the live region with the indices of fully visible slides in a Swiper instance.
	 */
	const updateLiveRegion = () => {

		const totalSlides = swiper.slides.length;
		const fullyVisibleSlides = Array.from( swiper.slides ).filter( ( slide ) =>
			slide.classList.contains( 'swiper-slide-visible' )
		);

		if ( fullyVisibleSlides.length > 0 ) {

			let indices = fullyVisibleSlides.map( ( slide ) =>
				parseInt( slide.dataset.swiperSlideIndex, 10 ) + 1
			);

			indices = [...new Set( indices )];
			indices.sort( ( a, b ) => a - b );

			const ranges = [];
			let start = indices[0];
			let end = indices[0];

			for ( let i = 1; i < indices.length; i++ ) {

				if ( indices[i] === end + 1 ) {

					end = indices[i];

				} else {

					ranges.push( start === end ? `${start}` : `${start}–${end}` );
					start = indices[i];
					end = indices[i];

				}

			}
			ranges.push( start === end ? `${start}` : `${start}–${end}` );

			const formatted = ranges.join( ' i ' );

			if ( liveRegion ) {

				const total = swiper.originalParams.loopedSlides || totalSlides;
				liveRegion.textContent = `Aktywne slajdy: ${formatted} z wszystkich ${total}`;

			}

		}

	};

	swiper.on( 'transitionEnd', updateLiveRegion );

	/**
	 * Sets `inert` on non-visible slides and removes it from visible ones.
	 */
	const updateSlidesInert = () => {

		if ( !swiper.slides ) {

			return;

		}

		swiper.slides.forEach( ( slide ) => {

			if ( slide.classList.contains( 'swiper-slide-visible' ) ) {

				slide.removeAttribute( 'inert' );

			} else {

				slide.setAttribute( 'inert', '' );

			}

		} );

	};

	swiper.on( 'transitionEnd', updateSlidesInert );

	/**
	 * Initializes accessibility settings for a Swiper instance.
	 * This function ensures that the Swiper container and its slides
	 * have appropriate ARIA attributes to improve accessibility for
	 * users relying on assistive technologies.
	 */
	const initAccessibility = () => {

		if ( ! swiper || ! swiper.slides || swiper.slides.length === 0 ) {

			return;

		}

		const wombatParent = swiper.el.closest( '[class*="wp-block-wombat"]' );

		if ( wombatParent ) {

			// Pairing headers with a slider

			const labelEl =
				wombatParent.querySelector( '.c-swiper-header__title' ) ||
				wombatParent.querySelector( '.b-posts-featured__title' ) ||
				wombatParent.querySelector( 'h2:not(.has-wcag-disabled)' );

			if ( labelEl ) {

				if ( ! labelEl.id ) {

					labelEl.id = `wombat-heading-${ Math.random().toString( 36 ).substr( 2, 9 ) }`;

				}

				const swiperContainer = swiper.el;
				if ( swiperContainer ) {

					swiperContainer.setAttribute( 'aria-labelledby', labelEl.id );

				}

			}

			// Keyboard activation

			wombatParent.addEventListener( 'focusin', () => {

				swiper.keyboard.enable();

			} );

			wombatParent.addEventListener( 'focusout', () => {

				swiper.keyboard.disable();

			} );

		}

		const swiperContainer = swiper.el;
		if ( swiperContainer ) {

			swiperContainer.setAttribute( 'role', 'region' );
			swiperContainer.setAttribute( 'aria-roledescription', 'Karuzela' );

		}

		Array.from( swiper.slides ).forEach( ( slide ) => {

			const ariaLabel = slide.getAttribute( 'aria-label' );

			if ( ariaLabel && /\d+\s*\/\s*\d+/.test( ariaLabel ) ) {

				slide.removeAttribute( 'aria-label' );

			}

			if ( slide.getAttribute( 'role' ) === 'group' ) {

				slide.removeAttribute( 'role' );

			}

		} );

	};

	initAccessibility();
	updateSlidesInert();

	return () => {

		try {

			swiper.off( 'transitionEnd', updateLiveRegion );
			swiper.off( 'transitionEnd', updateSlidesInert );
			swiper.off( 'init', initAccessibility );

		} catch {
		}

	};

};

export default setupSwiperAccessibility;
